import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, SubmitHandler } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { CustomPasswordField } from 'components/CustomPasswordField'
import { ResetPasswordModel } from 'utils/models/Auth'
import { getOrganisationId } from 'utils/organisation'
import { useResetUserPassword, hasValidationFailures } from 'utils/auth'
import AuthPageWrapper from './AuthPageWrapper'
import PasswordValidation from './PasswordValidation'
import * as ROUTE from 'utils/constants/clientRoutes'
import { passwordValidation } from 'utils/validation/passwords'
import { toast } from 'react-toastify'

const validationSchema = Yup.object<ResetPasswordModel>().shape({
  password: passwordValidation,
  confirmpassword: Yup.string()
    .nullable()
    .oneOf([Yup.ref('password'), null], 'Both passwords should match')
    .required('Confirm password is required'),
})

const ResetPassword = () => {
  const { mutateAsync: resetUserPassword } = useResetUserPassword()
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)
  const [passwordChange, setPasswordChange] = useState(false)
  const navigate = useNavigate()

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordModel>({
    resolver: yupResolver(validationSchema),
  })
  const search = useLocation().search
  const token = new URLSearchParams(search).get('token')
  const [showTokenTimeoutStage, setShowTokenTimeoutStage] = useState(false)
  const onPasswordChange = watch('password')

  const onSubmit: SubmitHandler<ResetPasswordModel> = async data => {
    const resetPasswordData = {
      resetToken: token,
      newPassword: data.password,
      organisationId: getOrganisationId(),
    }

    try {
      await resetUserPassword(resetPasswordData)
      setPasswordChange(true)
    } catch (error) {
      const isTokenError = await hasValidationFailures(error)
      if (isTokenError) {
        setShowTokenTimeoutStage(true)
        return
      }

      // Handle all other errors with generic error message
      toast.error('Unable to reset password')
    }
  }
  const tokenTimeoutMessage = (
    <>
      <h2 className="mt-5 mb-5 text-branding-primary">
        Password reset couldn’t be completed
      </h2>
      <div className="flex flex-col gap-4 pt-5 ">
        <p className="pl-1 font-medium text-xl">
          Your request to reset your password couldn’t be completed.
        </p>
        <p className="pl-1 font-medium text-xl">
          This usually happens because our security tokens, which keep your
          account safe, have expired.
        </p>
        <p className="pl-1 font-medium text-xl">
          Please click the link below to request a new password reset:
        </p>
      </div>
      <div className="w-full lg:w-80 my-auto mx-0">
        <button
          className="w-full py-4 button mt-8 bg-branding-secondary"
          onClick={() => navigate(ROUTE.FORGOT_PASSWORD)}
        >
          Send New Reset Link
        </button>
      </div>
    </>
  )

  return (
    <AuthPageWrapper
      title={!showTokenTimeoutStage ? 'Reset your password' : ''}
      description={!showTokenTimeoutStage ? 'Setting your password' : ''}
      showPanel={false}
    >
      {!showTokenTimeoutStage && (
        <>
          <PasswordValidation password={onPasswordChange!} />

          <form>
            <CustomPasswordField
              control={control}
              errors={errors?.password}
              name="password"
              label="Password"
              state={passwordVisible}
              setState={setPasswordVisible}
            />
            <CustomPasswordField
              control={control}
              errors={errors?.confirmpassword}
              name="confirmpassword"
              label="Confirm password"
              state={confirmPasswordVisible}
              setState={setConfirmPasswordVisible}
            />
          </form>
          <div className="w-full my-auto mx-0">
            <button
              type="submit"
              className="w-full button py-4 bg-branding-secondary"
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmitting && (
                <span className="spinner-border spinner-border-sm mr-1"></span>
              )}
              Reset password
            </button>
          </div>
        </>
      )}
      {showTokenTimeoutStage && tokenTimeoutMessage}
      {passwordChange && (
        <p className="mt-9 font-medium text-branding-secondary">
          Your password was changed successfully.
        </p>
      )}
    </AuthPageWrapper>
  )
}

export default ResetPassword
